import { LoaderCircle } from "lucide-react";
import { useEffect, useState } from "react";
import type { PhotoProgress } from "../../domain/photoProgress";

/** Use next to the affected content, with durable errors/recovery outside this status. */
export function OperationStatus({ title, detail, slowDetail, variant = "panel" }: { title: string; detail?: string; slowDetail?: string; variant?: "panel" | "inline" }) {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const timeout = window.setTimeout(() => setSlow(true), 15_000);
    return () => window.clearTimeout(timeout);
  }, []);
  return (
    <div className="ui-operation-status" data-variant={variant} role="status" aria-live="polite" aria-atomic="true">
      <LoaderCircle className="ui-button-spinner" size={20} aria-hidden="true" />
      <div>{variant === "inline" ? <span>{title}</span> : <strong>{title}</strong>}{detail && <p>{detail}</p>}{slow && slowDetail && <p>{slowDetail}</p>}</div>
    </div>
  );
}

export function PhotoUploadStatus({ progress }: { progress: PhotoProgress | null }) {
  if (!progress) return null;
  const total = progress.photos.length;
  const ready = progress.photos.filter((photo) => photo.stage === "ready").length;
  const uploading = progress.photos.filter((photo) => photo.stage === "uploading").length;
  const preparing = progress.photos.find((photo) => photo.stage !== "ready" && photo.stage !== "failed");
  const title = progress.phase === "preparing" ? "Preparing photos"
    : progress.phase === "saving" ? "Saving photos to your item"
    : progress.phase === "reconciling" ? "Confirming your photos were saved"
    : uploading > 0 ? `Uploading ${total === 1 ? "photo" : `${total} photos`}`
    : "Saving photos";
  const detail = progress.phase === "preparing"
    ? `${ready} of ${total} prepared. ${preparing?.stage === "converting" ? "Converting HEIC photo: " : "Preparing: "}${preparing?.fileName ?? "photos"}`
    : progress.phase === "saving" ? "Keep this tab open until saving finishes."
    : progress.phase === "reconciling" ? "Keep this tab open. You don’t need to add them again."
    : `${ready} of ${total} ready. Keep this tab open until saving finishes.`;
  return <OperationStatus title={title} detail={detail} slowDetail="This is taking longer than usual. We’re still waiting for a response and will show the result here." />;
}
